/**
 * Accent palettes — the user-selectable primary colour (Settings → Appearance).
 *
 * The portal ships navy as the default primary; the picker lets a user swap it
 * for one of a few corporate-safe alternates. Each accent is expressed as three
 * themed values that mirror the brand's navy derivations:
 *   • primary       — buttons / links / active states  (the picked hex)
 *   • primaryActive — pressed state                     (hex mixed 20% black)
 *   • primaryBg     — soft accent surface               (light: 9% hex on white,
 *                                                         dark:  30% hex on the dark surface)
 * On dark chrome the primary reads brighter (hex mixed 50% white), matching how
 * the base dark theme uses `navyBright`.
 *
 * These feed `tamagui.config.ts` as sub-themes (`light_<key>` / `dark_<key>`),
 * so a nested <Theme name={accent}> re-points every `$primary` / `$primaryActive`
 * / `$primaryBg` token without touching the fixed navy sidebar/login chrome.
 */
import { navy, surfacesDark } from './brand'

export type Localized = { ru: string; en: string }

// ── hex mixing (RN has no color-mix) ─────────────────────────────────────────
function clamp(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)))
}
function toRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}
function toHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((n) => clamp(n).toString(16).padStart(2, '0')).join('').toUpperCase()
}
/** Blend `base` toward `other` by `amount` (0..1 weight of `other`). */
function mix(base: string, other: string, amount: number): string {
  const [r1, g1, b1] = toRgb(base)
  const [r2, g2, b2] = toRgb(other)
  const k = 1 - amount
  return toHex(r1 * k + r2 * amount, g1 * k + g2 * amount, b1 * k + b2 * amount)
}

const WHITE = '#FFFFFF'
const BLACK = '#000000'

export interface Shades {
  primary: string
  primaryActive: string
  primaryBgLight: string
  primaryBgDark: string
  bright: string
}

function shadesFor(hex: string): Shades {
  return {
    primary: hex,
    primaryActive: mix(hex, BLACK, 0.2),
    primaryBgLight: mix(hex, WHITE, 0.91),
    primaryBgDark: mix(hex, surfacesDark.bgBase, 0.7),
    bright: mix(hex, WHITE, 0.5),
  }
}

// Navy keeps the exact brand-tuned values so the default is pixel-identical.
const NAVY_SHADES: Shades = {
  primary: navy.navy9,
  primaryActive: navy.navyActive,
  primaryBgLight: navy.navyBgLight,
  primaryBgDark: navy.navyBgDark,
  bright: navy.navyBright,
}

export interface AccentDef {
  key: string
  hex: string
  label: Localized
  shades: Shades
}

export const ACCENTS: AccentDef[] = [
  { key: 'navy', hex: navy.navy9, label: { ru: 'AS Navy', en: 'AS Navy' }, shades: NAVY_SHADES },
  { key: 'slate', hex: '#334155', label: { ru: 'Графит', en: 'Slate' }, shades: shadesFor('#334155') },
  { key: 'teal', hex: '#0F6E6E', label: { ru: 'Петроль', en: 'Teal' }, shades: shadesFor('#0F6E6E') },
  { key: 'forest', hex: '#2F6B43', label: { ru: 'Хвойный', en: 'Forest' }, shades: shadesFor('#2F6B43') },
  { key: 'wine', hex: '#7A3550', label: { ru: 'Бордо', en: 'Wine' }, shades: shadesFor('#7A3550') },
]

export const ACCENT_KEYS: string[] = ACCENTS.map((a) => a.key)
export const DEFAULT_ACCENT = 'navy'

// ── Sidebar / dark chrome ────────────────────────────────────────────────────
// The sidebar is a deep, near-black tint of the accent. Navy keeps its exact
// brand values (CHROME.navyTop / CHROME.navy) so the default is unchanged; the
// other accents derive a deep background + a slightly darker edge. White-alpha
// text/dividers and the bronze active accent (see CHROME in tones.ts) read fine
// over any of these, so they stay as-is.
export interface Chrome {
  bg: string
  bgDeep: string
}
const accentChromes: Record<string, Chrome> = ACCENTS.reduce((out, a) => {
  out[a.key] =
    a.key === 'navy'
      ? { bg: '#16265C', bgDeep: '#13245A' }
      : { bg: mix(a.hex, BLACK, 0.45), bgDeep: mix(a.hex, BLACK, 0.58) }
  return out
}, {} as Record<string, Chrome>)

export const accentChrome = (key: string): Chrome => accentChromes[key] ?? accentChromes[DEFAULT_ACCENT]

/** Theme fragments spread into `tamagui.config.ts` (sub-themes of light/dark). */
export type AccentTheme = { primary: string; primaryActive: string; primaryBg: string }
export const accentThemes: Record<string, AccentTheme> = ACCENTS.reduce((out, a) => {
  out[`light_${a.key}`] = {
    primary: a.shades.primary,
    primaryActive: a.shades.primaryActive,
    primaryBg: a.shades.primaryBgLight,
  }
  out[`dark_${a.key}`] = {
    primary: a.shades.bright,
    // hover/press of the BRIGHT primary — stays light enough for the dark
    // `$onPrimary` ink (the light-theme primaryActive is a dark hex → unreadable).
    primaryActive: mix(a.shades.bright, BLACK, 0.15),
    primaryBg: a.shades.primaryBgDark,
  }
  return out
}, {} as Record<string, AccentTheme>)
